import { action, computed, makeObservable, observable } from 'mobx';
import { IDeviceDetailsConstructorOptions } from './device-detail-entry-store';
import { AbstractDeviceDetailWithCommandCodeStore } from './device-detail-with-command-code-store';
import { ContainerDto } from 'dto/container.dto';
import { DEVICE_JOB_EXECUTION_STATUS } from 'dto/remote-commands';

export enum CONTAINER_ACTION {
    START = 'start',
    STOP = 'stop',
}

const MAX_VERIFY_RETRIES = 5;

export class DeviceContainersStore extends AbstractDeviceDetailWithCommandCodeStore<ContainerDto> {
    constructor(options: IDeviceDetailsConstructorOptions) {
        super('containers', { ...options, code: 'containers_listing' });
        makeObservable(this);
    }

    @observable
    private _pendingContainerIds = new Set<string>();

    /** ************************************
     * Override functions
     **************************************/
    protected async initializeData(): Promise<ContainerDto[]> {
        return this._devicesStore.getContainers(this.accountId, this.uid);
    }

    @computed
    public get currentContainersListing(): ContainerDto[] {
        return this.entities;
    }

    @computed
    public get pendingContainerIds(): ReadonlySet<string> {
        return this._pendingContainerIds;
    }

    public isPending(container: ContainerDto): boolean {
        return this._pendingContainerIds.has(container.id);
    }

    public async startContainer(container: ContainerDto): Promise<void> {
        await this._runContainerAction(container, CONTAINER_ACTION.START);
    }

    public async stopContainer(container: ContainerDto): Promise<void> {
        await this._runContainerAction(container, CONTAINER_ACTION.STOP);
    }

    private async _runContainerAction(container: ContainerDto, containerAction: CONTAINER_ACTION): Promise<void> {
        if (this.isPending(container)) return;
        this._setPending(container.id, true);
        try {
            const commandResult = await this._devicesStore.triggerRemoteCommand(this.accountId, {
                commandCode: 'remote_containers',
                targetDeviceNames: [this.uid],
                commandPayload: {
                    action: containerAction,
                    containers: [container.id]
                }
            });
            const statuses = await this.waitForExecutionCompletion(MAX_VERIFY_RETRIES, commandResult.executionId, [this.uid], 3);
            if (statuses[this.uid] === DEVICE_JOB_EXECUTION_STATUS.SUCCEEDED) {
                const reloadedContainers = await this._devicesStore.getContainers(this.accountId, this.uid);
                this._replaceContainer(reloadedContainers.find(item => item.id === container.id));
            }
        } finally {
            this._setPending(container.id, false);
        }
    }

    @action
    private _setPending(containerId: string, pending: boolean): void {
        const pendingIds = new Set(this._pendingContainerIds);
        if (pending) {
            pendingIds.add(containerId);
        } else {
            pendingIds.delete(containerId);
        }
        this._pendingContainerIds = pendingIds;
    }

    private _replaceContainer(updated?: ContainerDto): void {
        if (!updated) {
            return;
        }
        this.setEntities(this.entities.map((item) => (item.id === updated.id ? updated : item)));
    }
}
